import { useEffect, useRef, useState } from "react";
import type { MqttLiveMessage } from "../types/mqtt";

const MAX_MESSAGES = 20;

export function useMqttLive(topicPattern: string | null, enabled: boolean) {
  const [messages, setMessages] = useState<MqttLiveMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled || !topicPattern) {
      setMessages([]);
      setConnected(false);
      setError(null);
      esRef.current?.close();
      esRef.current = null;
      return;
    }

    setMessages([]);
    setError(null);
    setConnected(false);

    const url = `/api/mqtt/live?topic=${encodeURIComponent(topicPattern)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("subscribed", () => {
      setConnected(true);
      setError(null);
    });

    es.addEventListener("message", (ev) => {
      try {
        const msg = JSON.parse(ev.data) as MqttLiveMessage;
        setMessages((prev) => [...prev, msg].slice(-MAX_MESSAGES));
      } catch {
        /* ignore */
      }
    });

    es.addEventListener("error", (ev) => {
      if (ev instanceof MessageEvent && ev.data) {
        try {
          const data = JSON.parse(ev.data) as { error?: string };
          setError(data.error ?? "MQTT error");
        } catch {
          setError("MQTT stream error");
        }
      } else if (es.readyState === EventSource.CLOSED) {
        setConnected(false);
        setError((prev) => prev ?? "Stream closed");
      }
    });

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnected(false);
      }
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [topicPattern, enabled]);

  return { messages, connected, error };
}
